// components/Publishers/DashboardProfile.jsx
import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { buildPublisherPath } from "../../../utils/slugify";
import { Helmet } from "react-helmet-async";
import Header from "../../header";
import { Footer2 } from "../../footer2";
import InstructorSidebar from "../sidebar";
import { Link } from "react-router-dom";
import { publisherDetail } from "../../../services/publisherService";
import { getpublisherstemplatesandattributedatasearch } from "../../../services/publisherstemplatesandattributeService";
import { AppContext } from "../../../services/AuthContext";
import usePageTracking from "../../../hooks/usePageTracking";

// utils (dosyanın üstüne ekle)
const norm = (s) => (s ?? "").toString().toLowerCase().normalize("NFKD").trim();

const titleCaseTR = (s) => {
  if (!s) return "";
  const lower = s.toLocaleLowerCase("tr-TR");
  return lower.charAt(0).toLocaleUpperCase("tr-TR") + lower.slice(1);
};

const firstDataValue = (datajson) => {
  const v = datajson?.[0]?.value;
  return Array.isArray(v) ? v.filter(Boolean).join(", ") : "";
};

// attributesData içinden attrname **veya** label ile alan yakalayıcı
const pickAttrValue = (attributesData, names = []) => {
  const targets = names.map(norm);
  const hit = attributesData.find(a => {
    const rawName =
      a?.publisherdata?.attributedata?.templatetypeandattribute?.attribute?.attrname ||
      a?.publisherdata?.attributedata?.label || "";
    return targets.includes(norm(rawName));
  });
  return firstDataValue(hit?.datajson) || "";
};


// GÖSTERİLECEK ALANLAR (normalize edilmiş halleri)
const WANTED = ["web adresi", "adres", "ülke", "il", "e-mail", "email"];

const getNiceLabel = (name) => {
  const n = norm(name);
  if (n === "web adresi") return "Web Adresi";
  if (n === "adres") return "Adres";
  if (n === "ülke") return "Ülke";
  if (n === "il") return "İl";
  if (n === "e-mail" || n === "email") return "E-mail";
  return name || "-";
};

const normalizeUrl = (val) => {
  if (!val) return "";
  let url = val.trim();
  if (!/^https?:\/\//i.test(url)) url = "https://" + url;
  return url;
};

const DashboardProfile = () => {
  usePageTracking("publisher");
  const params = useParams();
  // support both routes: /publisher/:slug  and /publisher-detail/:id
  const rawId = params.id || (params.slug ? (String(params.slug).match(/-(\d+)$/) || [])[1] : null);
  const id = rawId;
  const { auth } = useContext(AppContext);

  const [profileData, setProfileData] = useState(null);
  const [attributesData, setAttributesData] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const data = await publisherDetail(id, 1, auth.token);
        if (data?.status === "OK") setProfileData(data.data);
      } catch (e) {
        console.error("Error fetching profile data:", e);
        setProfileData(null);
      }
    })();
    (async () => {
      try {
        const data = await getpublisherstemplatesandattributedatasearch(id, 1, auth.token);
        if (data?.status === "OK") setAttributesData(data.data || []);
      } catch (e) {
        console.error("Error fetching attributes data:", e);
        setAttributesData([]);
      }
    })();
  }, [id, auth?.token]);

  if (!profileData) return <p>Loading...</p>;

  // Sadece Web Adresi, Adres, Ülke, İl
  const wantedAttrs = attributesData
    .map((a) => {
      const attrName =
        a?.publisherdata?.attributedata?.templatetypeandattribute?.attribute?.attrname ||
        a?.publisherdata?.attributedata?.label || "";
      const value = firstDataValue(a?.datajson);
      return { attrName, value };
    })
    .filter((x) => WANTED.includes(norm(x.attrName)))
    .filter((x) => x.value && x.value.trim() !== "");

  // Açıklama (varsa)
  const detailsHtml = profileData?.detailedinformation?.tr || "";
  const hasDetails = !!detailsHtml.replace(/<[^>]+>/g, "").trim();
  const valWebsite = pickAttrValue(attributesData, ["Web Adresi", "Web adresi"]);
  const valAddress = pickAttrValue(attributesData, ["Adres"]);
  const valEmail = pickAttrValue(attributesData, ["E-mail", "Email"]);
  let valCountry = pickAttrValue(attributesData, ["Ülke"]);
  let valCity = pickAttrValue(attributesData, ["İl", "Şehir", "City"]);

  // Görsel/okunurluk düzeltmeleri
  if (valCountry) valCountry = titleCaseTR(valCountry); // "türkiye" -> "Türkiye"
  if (valCity) valCity = titleCaseTR(valCity);

  // SEO: canonical, Open Graph and JSON-LD (Organization)
  const canonical = window.location.origin + buildPublisherPath({ title: profileData.title, id: profileData.id });
  const websiteUrl = normalizeUrl(valWebsite);
  const sameAs = websiteUrl ? [websiteUrl] : [];
  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: profileData.title || undefined,
    url: canonical,
    sameAs: sameAs.length ? sameAs : undefined,
    description: hasDetails ? detailsHtml.replace(/<[^>]+>/g, "").trim() : undefined,
    address: valAddress ? {
      "@type": "PostalAddress",
      streetAddress: valAddress,
      addressCountry: valCountry || undefined,
      addressLocality: valCity || undefined
    } : undefined,
    contactPoint: valEmail ? [{
      "@type": "ContactPoint",
      email: valEmail,
      contactType: "customer support"
    }] : undefined
  };

  // Sadece dolu olanları göstereceğiz
  const publisherExtraRows = [
    { label: "Adres", value: valAddress, type: "text" },
    { label: "Web Adresi", value: valWebsite, type: "link" },
    { label: "E-mail", value: valEmail, type: "mailto" },
    { label: "Ülke", value: valCountry, type: "text" },
    { label: "İl", value: valCity, type: "text" },
  ].filter(r => r.value && r.value.trim() !== "");


  return (
    <div className="main-wrapper mt-5">
      <Helmet>
        <title>{`${profileData.title} | Publisher`}</title>
        <link rel="canonical" href={canonical} />
        <meta name="description" content={`${profileData.title} hakkında yayıncı bilgileri ve iletişim bilgileri.`} />
        <meta property="og:type" content="organization" />
        <meta property="og:title" content={profileData.title} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      </Helmet>
      <Header activeMenu={"My Profile"} />

      {/* breadcrumb KALDIRILDI */}

      <div className="page-content">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <InstructorSidebar />
            {/* /Sidebar */}

            <div className="col-xl-9 col-lg-9">
              {/* Üst Kart: Başlık, Kısa Adı, Üst Yayıncı, Açıklama (varsa) */}
              <div className="settings-widget card-details mb-0">
                <div className="settings-menu p-0">
                  <div className="profile-heading">
                    <h3>{profileData.title}</h3>
                  </div>

                  <div className="checkout-form personal-address">
                    <div className="row">
                      {profileData.sorttitle && (
                        <div className="col-sm-12">
                          <div className="contact-info">
                            <h6>Kısa Adı</h6>
                            <p>{profileData.sorttitle}</p>
                          </div>
                        </div>
                      )}

                      {profileData.parent && (
                        <div className="col-sm-12">
                          <div className="contact-info">
                            <h6>Üst Yayıncı</h6>
                            <Link className="text-sm" to={buildPublisherPath({ title: profileData.parent.title, id: profileData.parent.id })}>
                              {profileData.parent.title || "-"}
                            </Link>
                          </div>
                        </div>
                      )}

                      {hasDetails && (
                        <div className="col-sm-12">
                          <div className="contact-info mb-0">
                            <h6>Detaylı Bilgi</h6>
                            <div dangerouslySetInnerHTML={{ __html: detailsHtml }} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bilgiler: Web Adresi, Adres, Ülke, İl */}
              <div className="settings-widget card-details mt-3">
                <div className="settings-menu p-0">
                  <div className="profile-heading">
                    <h3>Ek Bilgiler</h3>
                  </div>
                  <div className="checkout-form personal-address">
                    <div className="row">
                      {publisherExtraRows.length === 0 ? (
                        <div className="col-sm-12">
                          <p className="text-muted mb-0">Yayıncıya ait bilgi bulunmamaktadır.</p>
                        </div>
                      ) : (
                        publisherExtraRows.map((r, idx) => (
                          <div className="col-sm-6" key={idx}>
                            <div className="contact-info">
                              <h6>{r.label}</h6>
                              {r.type === "link" ? (
                                <p>
                                  <a
                                    href={/^https?:\/\//i.test(r.value) ? r.value : `https://${r.value}`}
                                    target="_blank" rel="noopener noreferrer"
                                  >
                                    {r.value}
                                  </a>
                                </p>
                              ) : r.type === "mailto" ? (
                                <p><a href={`mailto:${r.value}`}>{r.value}</a></p>
                              ) : (
                                <p>{r.value}</p>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                  </div>
                </div>
              </div>
              {/* /Ek Bilgiler */}
            </div>
            {/* /col */}
          </div>
        </div>
      </div>

      <Footer2 />
    </div>
  );
};

export default DashboardProfile;
